<template>
  <a-layout class="pr-app-admin">
    <h2>{{ $t('admin.ROAR Window') }}</h2>
    <div class="header-actions">
      <a-button class="btn-add" @click="newPeriod">
        <template #icon>
          <plus-outlined />
        </template>
        {{ $t('admin.Create New Period') }}
      </a-button>
    </div>
    <a-table
      class="job-table"
      :rowKey="(record) => record.id"
      :columns="columns"
      :data-source="periodRecord"
      :pagination="false"
      :rowClassName="
        (record) => {
          const ts = parseInt(+new Date() / 1000, 0)
          if (record.status == 1) {
            return 'active'
          }
          if (record.publish_status > 0) {
            return 'disabled'
          }
        }
      "
    >
      <template v-slot:emptyText>
        <Empty />
      </template>
      <template v-slot:headerCell="{ title }">
        {{ $t('admin.' + title) }}
      </template>
      <template v-slot:bodyCell="{ column, record }">
        <template v-if="column.key === 'period_name'">
          <div style="display: flex; align-items: center">
            <a-badge status="success" v-if="record.status == 1" />
            <a-badge status="default" v-else />
            <p style="padding-left: 3px; margin-bottom: 0; text-align: left">
              {{ record.period_name }}
            </p>
          </div>
        </template>
        <template v-else-if="column.key === 'period_type'">
          <span>{{ parseInt(record.offline_mode) === 1 ? $t('admin.Offline') : $t('admin.Online') }}</span>
        </template>
        <template v-else-if="['employee', 'manage', 'hrbp', 'result'].includes(column.key)">
          <span>{{ parseInt(record.offline_mode) === 1 ? '-' : record[column.key] }}</span>
        </template>
        <template v-if="column.key === 'action'">
          <a-button
            type="link"
            @click="editPeriod(record)"
            v-if="record.can_edit"
          >
            {{ $t('admin.Edit') }}
          </a-button>
          <template v-if="record.can_delete == 1">
            <!-- <span class="color-primary">|</span> -->
            &nbsp; &nbsp; &nbsp;
            <a-button type="link" @click="delPeriod(record)">
              {{ $t('admin.Delete') }}
            </a-button>
          </template>
        </template>
        <template v-else-if="column.key === 'public'">
          <a-button
            type="link"
            @click="publishManager(record)"
            v-if="record.status == 1"
            :disabled="!record.can_publish_lm"
          >
            {{ $t('admin.Publish Manager') }}
          </a-button>
          <a-button
            type="link"
            @click="publicPeriod(record)"
            v-if="record.status == 1"
            :disabled="!record.can_publish"
          >
            {{ $t('admin.Publish Employee') }}
          </a-button>
        </template>
      </template>
    </a-table>
    <div class="footer-actions">
      <div class="page-pagination">
        <a-pagination
          @change="fetchData"
          v-model:current="pagination.current"
          :total="pagination.total"
          v-model:pageSize="pagination.pageSize"
          :pageSizeOptions="pagination.pageSizeOptions"
          size="small"
          show-less-items
          show-size-changer
          show-quick-jumper
        />
      </div>
    </div>

    <a-modal
      v-model:visible="periodVisible"
      centered
      :maskClosable="false"
      width="602px"
      :title="
        currentPeriod.id == 'new'
          ? $t('admin.' + 'New Period')
          : $t('admin.' + 'Edit Period')
      "
      class="period-form-dialog"
      @ok="savePeriod"
      :ok-text="$t('admin.Save')"
      :cancel-text="$t('admin.Cancel')"
      @cancel="$refs.form.clearValidate()"
    >
      <a-form
        layout="vertical"
        ref="form"
        :model="currentPeriod"
        :rules="validateRules"
      >
        <a-form-item :label="$t('admin.Period Name')" name="period_name">
          <a-input
            v-model:value="currentPeriod.period_name"
            :placeholder="$t('admin.Please Input')"
          ></a-input>
        </a-form-item>
        <a-form-item :label="$t('admin.Period Type')">
          <a-radio-group v-model:value="currentPeriod.offline_mode" :disabled="currentPeriod.id !== 'new' && currentPeriod.status === 1">
            <a-radio :value="0">{{ $t('admin.Online') }}</a-radio>
            <a-radio :value="1">{{ $t('admin.Offline') }}</a-radio>
          </a-radio-group>
          <div v-if="currentPeriod.id !== 'new' && currentPeriod.status === 1" style="color: #999; font-size: 12px; margin-top: 4px;">
            {{ $t('admin.Period type cannot be changed when status is active') }}
          </div>
        </a-form-item>
        <a-row :gutter="30" align="bottom">
          <a-col :span="12">
            <a-form-item name="roar_window_start">
              <template #label>
                {{ $t('admin.ROAR Window') }}
                <a-tooltip>
                  <template #title>
                    {{
                      $t(
                        'admin.The entire timeline for Project ROAR, from project start day to the day it ends (several days after Panel Result Submission end day).'
                      )
                    }}
                  </template>
                  <QuestionCircleFilled class="question-circle" />
                </a-tooltip>
              </template>
              <a-date-picker
                :format="'MM/DD/YYYY'"
                v-model:value="currentPeriod.roar_window_start"
                :placeholder="$t('admin.Select date')"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item name="roar_window_end">
              <a-date-picker
                :format="'MM/DD/YYYY'"
                v-model:value="currentPeriod.roar_window_end"
                :placeholder="$t('admin.Select date')"
              />
            </a-form-item>
          </a-col>
        </a-row>
        <template v-if="currentPeriod.offline_mode === 0">
          <a-row :gutter="30" align="bottom">
            <a-col :span="12">
              <a-form-item name="employee_start">
                <template #label>
                  {{ $t('admin.Employee Application Period') }}

                  <a-tooltip>
                    <template #title>
                      {{
                        $t(
                          'admin.From the start day to the last day that requires the employee to submit application.'
                        )
                      }}
                    </template>
                    <QuestionCircleFilled class="question-circle" />
                  </a-tooltip>
                </template>
                <a-date-picker
                  :format="'MM/DD/YYYY'"
                  v-model:value="currentPeriod.employee_start"
                  :placeholder="$t('admin.Select date')"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item name="employee_end">
                <a-date-picker
                  :format="'MM/DD/YYYY'"
                  v-model:value="currentPeriod.employee_end"
                  :placeholder="$t('admin.Select date')"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="30" align="bottom">
            <a-col :span="12">
              <a-form-item name="manage_start">
                <template #label>
                  {{ $t('admin.Review Manager Assessment Period') }}
                  <a-tooltip>
                    <template #title>
                      {{
                        $t(
                          'admin.From the start day to the last day that requires the manager to submit assessment (several days after "Employee Application" end day).'
                        )
                      }}
                    </template>
                    <QuestionCircleFilled class="question-circle" />
                  </a-tooltip>
                </template>
                <a-date-picker
                  :format="'MM/DD/YYYY'"
                  v-model:value="currentPeriod.manage_start"
                  :placeholder="$t('admin.Select date')"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item name="manage_end">
                <a-date-picker
                  :format="'MM/DD/YYYY'"
                  v-model:value="currentPeriod.manage_end"
                  :placeholder="$t('admin.Select date')"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="30" align="bottom">
            <a-col :span="12">
              <a-form-item name="hrbp_start">
                <template #label>
                  {{ $t('admin.HRBP Review Period') }}
                  <a-tooltip>
                    <template #title>
                      {{
                        $t(
                          'admin.From the start day to the last day that allows HRBP to reopen assessment (several days after "Manager Assessment" end day). Application approval status will be published after HRBP Review end day.'
                        )
                      }}
                    </template>
                    <QuestionCircleFilled class="question-circle" />
                  </a-tooltip>
                </template>
                <a-date-picker
                  :format="'MM/DD/YYYY'"
                  v-model:value="currentPeriod.hrbp_start"
                  :placeholder="$t('admin.Select date')"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item name="hrbp_end">
                <a-date-picker
                  :format="'MM/DD/YYYY'"
                  v-model:value="currentPeriod.hrbp_end"
                  :placeholder="$t('admin.Select date')"
                  @change="
                    (val) => {
                      this.currentPeriod.result_start = val.add(1, 'day')
                    }
                  "
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="30" align="bottom">
            <a-col :span="12">
              <a-form-item name="result_start">
                <template #label>
                  {{ $t('admin.Panel Result Submission Period') }}

                  <a-tooltip>
                    <template #title>
                      {{
                        $t(
                          'admin.From one day after HRBP Review end day to the last day that requires HRBP to upload panel result after getting global confirmation.'
                        )
                      }}
                    </template>
                    <QuestionCircleFilled class="question-circle" />
                  </a-tooltip>
                </template>
                <a-date-picker
                  :format="'MM/DD/YYYY'"
                  v-model:value="currentPeriod.result_start"
                  :placeholder="$t('admin.Select date')"
                  @change="
                    (val) => {
                      this.currentPeriod.hrbp_end = val.subtract(1, 'day')
                    }
                  "
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item name="result_end">
                <a-date-picker
                  :format="'MM/DD/YYYY'"
                  v-model:value="currentPeriod.result_end"
                  :placeholder="$t('admin.Select date')"
                />
              </a-form-item>
            </a-col>
          </a-row>
        </template>
      </a-form>
    </a-modal>
  </a-layout>
</template>
<script>
import { mapGetters } from 'vuex'
import { message, Modal } from 'ant-design-vue'
import { QuestionCircleFilled, PlusOutlined } from '@ant-design/icons-vue'
import {
  getCycleList,
  addCycle,
  deleteCycle,
  updateCycle,
  publishCycle,
  publishLM,
} from '@/api/admin'
import dayjs from 'dayjs'
import { toRaw } from '@vue/reactivity'
import Empty from '@/components/Empty'
export default {
  components: { QuestionCircleFilled, PlusOutlined, Empty },
  data() {
    const columns = [
      { title: 'Period Name', key: 'period_name' },
      { title: 'Period Type', key: 'period_type' },
      { title: 'ROAR Window', key: 'roar_window' },
      { title: 'Employee Application Period', key: 'employee' },
      { title: 'Review Manager Assessment Period', key: 'manage' },
      { title: 'HRBP Assessment Period', key: 'hrbp' },
      { title: 'Panel Result Upload Period', key: 'result' },
      { title: 'Action', key: 'action', width: 190 },
      { title: 'Publish Result', key: 'public' },
    ].map((item) => {
      item.dataIndex = item.key
      return item
    })
    const validateRules = {}

    const inputFields = ['period_name']

    const dateFields = [
      'roar_window_start',
      'roar_window_end',
      'employee_start',
      'employee_end',
      'manage_start',
      'manage_end',
      'hrbp_start',
      'hrbp_end',
      'result_start',
      'result_end',
    ]
    dateFields.map((item) => {
      validateRules[item] = [
        {
          required: true,
          validator: (_rule, value) => {
            if (value == null || !value.isValid()) {
              return Promise.reject(this.$t('admin.Select date'))
            } else {
              return Promise.resolve()
            }
          },
          trigger: 'change',
        },
      ]
    })

    inputFields.map((item) => {
      validateRules[item] = [
        {
          required: true,
          message: this.$t('admin.Please Input'),
          trigger: 'blur',
        },
      ]
    })
    return {
      pagination: {
        total: 0,
        current: 1,
        pageSize: 10,
        pageSizeOptions: ['10', '30', '50'],
      },
      columns,
      periodVisible: false,
      currentPeriod: {},
      periodRecord: [],
      validateRules,
    }
  },
  watch: {
    'currentPeriod.employee_start'(newVal) {
      this.currentPeriod.employee_start = newVal
      this.currentPeriod.manage_start = newVal
      this.currentPeriod.hrbp_start = newVal
    },
    'currentPeriod.manage_start'(newVal) {
      this.currentPeriod.employee_start = newVal
      this.currentPeriod.manage_start = newVal
      this.currentPeriod.hrbp_start = newVal
    },
    'currentPeriod.hrbp_start'(newVal) {
      this.currentPeriod.employee_start = newVal
      this.currentPeriod.manage_start = newVal
      this.currentPeriod.hrbp_start = newVal
    },
  },
  computed: {
    ...mapGetters({
      ability: 'acl/ability',
    }),
    canPublish() {
      const ability = this.ability.find((item) => item.key === 'can_publish')
      return ability.value
    },
    allStartTime: {
      get: function () {
        return (
          this.currentPeriod.manage_start ||
          this.currentPeriod.employee_start ||
          this.currentPeriod.hrbp_start
        )
      },
      set: function (val) {
        this.currentPeriod.manage_start = val
        this.currentPeriod.employee_start = val
        this.currentPeriod.hrbp_start = val
      },
    },
  },
  created() {
    this.fetchData()
  },
  methods: {
    fetchData() {
      let params = {
        size: this.pagination.pageSize || 10,
        page: this.pagination.current || 1,
      }
      getCycleList(params).then((res) => {
        if (res.code === 200) {
          // 后端目前没有返回 offline_mode 字段，默认为线上模式(0)
          // TODO: 需要后端在 API 中返回 offline_mode 字段
          this.periodRecord = res.data.map(item => ({
            ...item,
            offline_mode: item.offline_mode !== undefined ? item.offline_mode : 0
          }))
          this.pagination.total = res.page.total || 0
        }
      })
    },
    newPeriod() {
      this.periodVisible = true
      this.currentPeriod = { id: 'new', offline_mode: 0 }
    },
    stringDateToArrayDate(datestring) {
      return datestring
        .replace(/[\r\n]/g, '')
        .replace(/ +/g, '')
        .split('-')
        .map((item) => {
          return dayjs(item, 'MM/DD/YYYY')
        })
    },
    arrayDateToStringDate(arrayDate) {
      console.log(arrayDate)
      return `${arrayDate[0].format('MM/DD/YYYY')} - ${arrayDate[1].format(
        'MM/DD/YYYY'
      )}`
    },
    editPeriod(period) {
      console.log('editPeriod - offline_mode:', period.offline_mode, 'type:', typeof period.offline_mode)

      this.currentPeriod = {
        id: period.id,
        period_name: period.period_name,
        offline_mode: parseInt(period.offline_mode) || 0,
        status: period.status,
      }

      console.log('currentPeriod.offline_mode after parse:', this.currentPeriod.offline_mode)

      // 离线模式只需要加载 ROAR Window 时间
      if (parseInt(period.offline_mode) === 1) {
        const dateData = this.stringDateToArrayDate(period.roar_window)
        this.currentPeriod.roar_window_start = dateData[0]
        this.currentPeriod.roar_window_end = dateData[1]
      } else {
        // 线上模式需要加载所有时间字段
        const keys = ['roar_window', 'employee', 'manage', 'hrbp', 'result']
        keys.map((key) => {
          const dateData = this.stringDateToArrayDate(period[key])
          this.currentPeriod[`${key}_start`] = dateData[0]
          this.currentPeriod[`${key}_end`] = dateData[1]
        })
      }

      this.periodVisible = true
    },
    delPeriod(period) {
      deleteCycle(period.id).then((res) => {
        if (res.code == 200) {
          message.success(this.$t('admin.Deleted successfully'))
          this.fetchData()
        } else {
          message.error(res.msg)
        }
      })
    },
    publicPeriod(period) {
      const that = this
      Modal.confirm({
        title: that.$t('admin.Publish Result'),
        okText: that.$t('admin.Yes'),
        cancelText: that.$t('admin.No'),
        closable: true,
        content: that.$t('admin.Are you sure you want to publish result?'),
        onOk() {
          publishCycle(period.id, period.offline_mode).then((res) => {
            if (res.code == 200) {
              message.success(that.$t('admin.Published successfully'))
              that.fetchData()
            } else {
              message.error(res.msg)
            }
          })
        },
      })
    },
    publishManager(record) {
      const that = this
      Modal.confirm({
        title: that.$t('admin.Publish Result'),
        okText: that.$t('admin.Yes'),
        cancelText: that.$t('admin.No'),
        closable: true,
        content: that.$t('admin.Are you sure you want to publish result?'),
        onOk() {
          publishLM(record.id, record.offline_mode).then((res) => {
            if (res.code == 200) {
              message.success(that.$t('admin.Published successfully'))
              that.fetchData()
            } else {
              message.error(res.msg)
            }
          })
        },
      })
    },
    async savePeriod() {
      try {
        await this.$refs.form.validate()
        const data = toRaw(this.currentPeriod)

        // 如果是线下申请，只需要验证基本字段
        if (data.offline_mode === 1) {
          const params = {
            id: data.id,
            phase: data.period_name,
            offline_mode: data.offline_mode,
            plan_st: data['roar_window_start'].format('MM/DD/YYYY'),
            plan_et: data['roar_window_end'].format('MM/DD/YYYY'),
          }
          const vm = this

          if (params.id == 'new') {
            addCycle(params).then(
              (res) => {
                message.success('Created successfully')
                this.periodVisible = false
                getCycleList().then((res) => {
                  this.periodRecord = res.data
                  vm.fetchData()
                })
              },
              (err) => {
                message.error(err.msg)
              }
            )
          } else {
            updateCycle(params).then(
              (res) => {
                message.success(this.$t('admin.Updated successfully'))
                this.periodVisible = false
                vm.fetchData()
              },
              (err) => {
                message.error(err.msg)
              }
            )
          }
          return
        }

        // 线上申请的验证逻辑
        const hasEmpty =
          Object.keys(data).find((item) => {
            if (data[item] == null) {
              console.log(item)
              return true
            } else {
              return false
            }
          }) != null

        if (hasEmpty) {
          return message.info('Please complete all the fields.')
        }

        if (
          this.currentPeriod.employee_start.isAfter(
            this.currentPeriod.employee_end
          )
        ) {
          return message.info(
            'The employee application end time cannot be less than the start time.'
          )
        }
        if (
          this.currentPeriod.manage_start.isAfter(this.currentPeriod.manage_end)
        ) {
          return message.info(
            'The manager assessment end time cannot be less than the start time.'
          )
        }
        if (
          this.currentPeriod.hrbp_start.isAfter(this.currentPeriod.hrbp_end)
        ) {
          return message.info(
            'The HRBP review end time cannot be less than the start time.'
          )
        }
        if (
          this.currentPeriod.result_start.isAfter(this.currentPeriod.result_end)
        ) {
          return message.info(
            'The panel resultsubmission end time cannot be less than the start time.'
          )
        }

        if (
          this.currentPeriod.hrbp_end.isAfter(this.currentPeriod.result_end)
        ) {
          return message.info(
            'The deadline for HRBP review cannot be greater than the panel result submission start date.'
          )
        }
        const params = {
          id: data.id,
          phase: data.period_name,
          offline_mode: data.offline_mode || 0,
          plan_st: data['roar_window_start'].format('MM/DD/YYYY'),
          plan_et: data['roar_window_end'].format('MM/DD/YYYY'),
          apply_st: data['employee_start'].format('MM/DD/YYYY'),
          apply_et: data['employee_end'].format('MM/DD/YYYY'),
          lm_st: data['manage_start'].format('MM/DD/YYYY'),
          lm_et: data['manage_end'].format('MM/DD/YYYY'),
          hrbp_review_st: data['hrbp_start'].format('MM/DD/YYYY'),
          hrbp_review_et: data['hrbp_end'].format('MM/DD/YYYY'),
          hrbp_submit_st: data['result_start'].format('MM/DD/YYYY'),
          hrbp_submit_et: data['result_end'].format('MM/DD/YYYY'),
        }
        const vm = this

        if (params.id == 'new') {
          addCycle(params).then(
            (res) => {
              message.success('Created successfully')
              this.periodVisible = false
              getCycleList().then((res) => {
                this.periodRecord = res.data
                vm.fetchData()
              })
            },
            (err) => {
              message.error(err.msg)
            }
          )
        } else {
          updateCycle(params).then(
            (res) => {
              message.success(this.$t('admin.Updated successfully'))
              this.periodVisible = false

              vm.fetchData()
            },
            (err) => {
              message.error(err.msg)
            }
          )
        }
      } catch (error) {
        console.log(error)
      }
    },
  },
}
</script>
<style lang="less">
@import url('@/styles/params.less');

.pr-app-admin {
  margin: @app-conetent-margin;
  background-color: #fff;
  padding: 24px;
  text-align: left;

  h2 {
    font-size: 20px;
    text-align: left;
    // font-family: Helvetica-Bold, Helvetica;
    font-weight: bold;
    color: #333333;
    line-height: 28px;
  }

  .header-actions {
    margin: 16px 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .footer-actions {
    margin: 16px 0;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
}

.question-circle {
  margin-left: 4px;
}

.ant-modal-body {
  max-height: 610px !important;
}

.period-form-dialog {
  .form-item {
    &:nth-child(1) {
      margin-top: -8px;
    }
  }

  .form-item-title {
    margin: 8px 0;
    font-size: 14px;
    font-family: Helvetica;
    color: #333333;
    line-height: 22px;
  }

  .form-content {
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;

    .ant-picker {
      width: 50%;

      &:nth-child(1) {
        margin-right: 30px;
      }
    }
  }
}

.active {
  background-color: #ebe7dd7b;
}

.disabled {
  color: #ccc;
}

// .ant-form-item-explain-error {
//   display: none;
// }
</style>
